import React from 'react';
import { Link } from 'react-router-dom';
import { useReviews } from '../hooks/useReviews';

const F = "'Inter', system-ui, sans-serif";

function Stars({ rating }) {
  return (
    <span style={starStyles.row} aria-label={`${rating} von 5 Sternen`}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{ ...starStyles.star, color: i <= rating ? '#f59e0b' : '#d1d5db' }}>★</span>
      ))}
    </span>
  );
}

const starStyles = {
  row: { display: 'inline-flex', gap: '1px' },
  star: { fontSize: '1.1rem', lineHeight: 1 },
};

export default function Referenzen() {
  const { reviews, loading, error } = useReviews();

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  return (
    <main style={styles.page}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div style={styles.pageHeaderInner}>
          <span style={styles.eyebrow}>Was unsere Gäste sagen</span>
          <h1 style={styles.heading}>Gästebewertungen</h1>
          <p style={styles.sub}>Echte Erfahrungen unserer Gäste – wir freuen uns über jeden Besuch!</p>
          {avgRating && (
            <div style={styles.avgRow}>
              <Stars rating={Math.round(Number(avgRating))} />
              <span style={styles.avgText}>{avgRating} von 5 · {reviews.length} Bewertung{reviews.length !== 1 ? 'en' : ''}</span>
            </div>
          )}
        </div>
      </div>

      <div style={styles.inner}>
        {loading && <p style={styles.info}>Bewertungen werden geladen...</p>}
        {error && <p style={styles.errorText}>Bewertungen konnten nicht geladen werden.<br /><small>{error}</small></p>}

        {!loading && !error && reviews.length === 0 && (
          <p style={styles.empty}>Noch keine Bewertungen vorhanden.</p>
        )}

        {!loading && !error && reviews.length > 0 && (
          <div style={styles.grid}>
            {reviews.map(review => (
              <div key={review.id} style={styles.card}>
                <div style={styles.cardTop}>
                  <Stars rating={review.rating || 5} />
                  <span style={styles.date}>{review.date}</span>
                </div>
                <p style={styles.text}>„{review.text}"</p>
                <div style={styles.author}>
                  <div style={styles.avatar}>
                    {(review.name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={styles.authorName}>{review.name}</div>
                    {review.origin && <div style={styles.authorOrigin}>{review.origin}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <section style={styles.ctaSection}>
          <h2 style={styles.ctaHeading}>Überzeugt? Planen Sie Ihren Aufenthalt!</h2>
          <p style={styles.ctaText}>Wir freuen uns darauf, Sie auf dem Lechcamping Spirkenhof begrüßen zu dürfen.</p>
          <div style={styles.ctaButtons}>
            <Link to="/buchung" style={styles.ctaBtn}>Jetzt anfragen</Link>
            <Link to="/galerie" style={styles.ctaBtnSecondary}>Galerie ansehen</Link>
          </div>
        </section>
      </div>
    </main>
  );
}

const styles = {
  page: { backgroundColor: 'white', minHeight: '70vh' },

  pageHeader: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    padding: '4rem 1.5rem 3.5rem',
    textAlign: 'center',
  },
  pageHeaderInner: { maxWidth: '640px', margin: '0 auto' },
  eyebrow: { fontSize: '0.75rem', fontFamily: F, fontWeight: '600', letterSpacing: '1.8px', textTransform: 'uppercase', color: '#1e8449' },
  heading: { color: '#111827', fontFamily: F, fontSize: 'clamp(1.9rem,5vw,2.8rem)', fontWeight: '700', marginTop: '0.4rem', marginBottom: '0.7rem', letterSpacing: '-0.3px' },
  sub: { color: '#6b7280', fontFamily: F, fontSize: '1rem', lineHeight: '1.6', marginBottom: '1.25rem' },
  avgRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', marginTop: '0.5rem' },
  avgText: { color: '#374151', fontFamily: F, fontSize: '0.9rem', fontWeight: '500' },

  inner: { maxWidth: '1100px', margin: '0 auto', padding: '3rem 1.5rem 4rem' },

  info: { textAlign: 'center', color: '#1e8449', fontFamily: F, padding: '3rem', fontSize: '1rem' },
  errorText: { textAlign: 'center', color: '#dc3545', fontFamily: F, padding: '2rem' },
  empty: { textAlign: 'center', color: '#9ca3af', fontFamily: F, padding: '3rem', fontSize: '1rem' },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1.5rem',
    marginBottom: '4rem',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    padding: '1.75rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  date: { color: '#9ca3af', fontFamily: F, fontSize: '0.82rem' },
  text: {
    color: '#374151',
    fontFamily: F,
    fontSize: '0.95rem',
    lineHeight: '1.7',
    fontStyle: 'italic',
    flex: 1,
  },
  author: { display: 'flex', alignItems: 'center', gap: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #f3f4f6' },
  avatar: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    backgroundColor: '#dcfce7',
    color: '#15803d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    fontWeight: '700',
    fontFamily: F,
    flexShrink: 0,
  },
  authorName: { color: '#111827', fontFamily: F, fontSize: '0.9rem', fontWeight: '600' },
  authorOrigin: { color: '#9ca3af', fontFamily: F, fontSize: '0.8rem', marginTop: '0.1rem' },

  ctaSection: { backgroundColor: '#f8f9fa', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center', border: '1px solid #e5e7eb' },
  ctaHeading: { color: '#1a1a1a', fontFamily: F, fontSize: '1.6rem', fontWeight: '700', marginBottom: '0.6rem', letterSpacing: '-0.3px' },
  ctaText: { color: '#6b7280', fontFamily: F, fontSize: '0.97rem', marginBottom: '1.75rem' },
  ctaButtons: { display: 'flex', gap: '0.85rem', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn: { display: 'inline-block', backgroundColor: '#1e8449', color: 'white', textDecoration: 'none', padding: '0.85rem 1.75rem', borderRadius: '8px', fontWeight: '600', fontFamily: F, fontSize: '0.95rem' },
  ctaBtnSecondary: { display: 'inline-block', backgroundColor: 'transparent', color: '#1e8449', textDecoration: 'none', padding: '0.82rem 1.75rem', borderRadius: '8px', fontWeight: '500', fontFamily: F, fontSize: '0.9rem', border: '1.5px solid #e5e7eb' },
};
